import { Grid, InputAdornment } from '@mui/material';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import uniqid from 'uniqid';
import { Wallet } from '../../apiTypes';
import EditModal from '../../components/EditModal';
import FormPriceField from '../../components/forms/FormPriceField';
import FormSelect from '../../components/forms/FormSelect';
import FormSwitch from '../../components/forms/FormSwitch';
import FormTextField from '../../components/forms/FormTextField';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useCafe } from '../../contexts/cafe.context';
import { useLocale } from '../../contexts/locale.context';
import { deleteDoc, setDoc, updateDoc } from '../../utils/api';

type Entity = Wallet;

type Props = {
  open: boolean;
  entity?: DbDocument<Entity> | null;
  onClose: () => void;
};

const EditWallet = ({ entity, open, onClose }: Props) => {
  const { cafeSlug, cafe, isPro } = useAdmin();
  const { lang } = useLocale();
  const { legalEntities, legalEntitiesMap } = useCafe();

  const legalEntityIds = useMemo(() => ['', ...legalEntities.map((i) => i.id)], [legalEntities]);

  const form = useForm<Entity>({
    mode: 'onChange',
    defaultValues: {
      name: entity?.name || '',
      hidden: entity?.hidden || false,
      balance: Math.round(entity?.balance || 0),
      bankPercent: entity?.bankPercent || 0,
      legalEntityId: entity?.legalEntityId || '',
      forCardPayments: entity?.forCardPayments || false,
      forCashPayments: entity?.forCashPayments || false,
    },
  });

  const handleClose = () => {
    onClose();
    form.reset();
  };

  const saveChanges = async (values: Entity) => {
    const payload: Entity = {
      ...values,
      balance: Number(values.balance),
      bankPercent: Number(values.bankPercent) || 0,
    };

    if (entity?.id) {
      await updateDoc<Entity>(`cafes/${cafeSlug}/wallets/${entity.id}`, payload);
    } else {
      await setDoc<Entity>(`cafes/${cafeSlug}/wallets/${uniqid()}`, payload);
    }

    handleClose();
  };

  const removeEntity = async () => {
    if (entity?.id) {
      await deleteDoc(`cafes/${cafeSlug}/wallets/${entity.id}`);
      handleClose();
    }
  };

  return (
    <EditModal<Entity>
      title={t.wallet[lang]}
      open={open}
      onClose={handleClose}
      submitButton={entity ? t.save_changes[lang] : t.add[lang]}
      onSubmit={saveChanges}
      secondaryButton={entity ? t.remove[lang] : undefined}
      confirmText={t.remove_wallet_confirm[lang]}
      onSecondaryButtonClick={removeEntity}
      form={form}
      size="xs"
    >
      <Grid container spacing={3}>
        <Grid item xs={12} sm={12} justifyContent="center">
          <FormTextField name="name" label={t.name[lang]} rules={{ required: true }} fullWidth />
        </Grid>

        <Grid item xs={12} sm={12}>
          <FormPriceField
            name="balance"
            label={t.balance[lang]}
            fullWidth
            disabled={!!entity?.id}
            InputProps={{ startAdornment: <InputAdornment position="start">{cafe?.currency}</InputAdornment> }}
          />
        </Grid>

        <Grid item xs={12} sm={12}>
          <FormTextField
            name="bankPercent"
            type="number"
            label={t.bank_commission[lang]}
            fullWidth
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
          />
        </Grid>

        {legalEntities.length > 0 && isPro && (
          <Grid item xs={12} sm={12}>
            <FormSelect
              label={t.legal_entity[lang]}
              name="legalEntityId"
              options={legalEntityIds}
              renderOption={(id) => legalEntitiesMap[id]?.name || id || t.any[lang]}
            />
          </Grid>
        )}

        <Grid item xs={12} sm={12}>
          <FormSwitch name="hidden" label={t.hidden[lang]} />
        </Grid>

        <Grid item xs={12} sm={12}>
          <FormSwitch name="forCashPayments" label={t.use_for_cash_orders[lang]} />
        </Grid>

        <Grid item xs={12} sm={12}>
          <FormSwitch name="forCardPayments" label={t.use_for_card_orders[lang]} />
        </Grid>
      </Grid>
    </EditModal>
  );
};

export default EditWallet;
